/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef } from 'react'
import { PerioVoiceHighlight } from '@utils/perio-keyword.utils'
import PerioHistory from './PerioHistory.component'
import {
  getProbeSiteLabels,
  getToothIndex,
  toDisplayOrder,
} from '@utils/perio.utils'
import { PerioSection } from '@dataTypes/chartingFeature.types'
import { getChartHistoryRowId } from '@utils/chart-history-selection.utils'
import { validatePerioData } from '@utils/validatePerioData'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useActivePerioChart } from '@hooks/useActivePerioChart'
import {
  GridComponent,
  RecordClickEventArgs,
  RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids'
import {
  setPerioUpdated,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import { useClinicChartingPreferences } from '@hooks/useClinicChartingPreferences'
import ToothNumberLabel from '@components/pages/Charting/Dental/ToothNumberLabel.component'

import { logger } from '@utils/safeLogger'

interface PerioChartProps {
  voiceCursor?: PerioVoiceHighlight
  chartIndex: number
  onViewNote: (id: string) => void
  isFullscreen: boolean
  gridRef: React.RefObject<GridComponent>
}

const PerioChart = ({
  voiceCursor,
  chartIndex,
  onViewNote,
  isFullscreen,
  gridRef,
}: PerioChartProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const cursorCell = useRef<HTMLTableCellElement>(null)
  useEffect(() => {
    // Follow the cursor inside the scrollable chart without moving keyboard focus.
    cursorCell.current?.scrollIntoView?.({
      block: 'nearest',
      inline: 'nearest',
    })
  }, [
    voiceCursor?.tooth,
    voiceCursor?.section,
    voiceCursor?.metric,
    chartIndex,
    isFullscreen,
  ])
  const { showToothNumber, chartingSystem } = useClinicChartingPreferences()
  const { activePerioChart } = useActivePerioChart()
  const perioCharts = useAppSelector((state) => state.charting.perioCharts)
  const toothNumbersUpper = Array.from({ length: 16 }, (_, i) => i + 1)
  const toothNumbersLower = Array.from({ length: 16 }, (_, i) => 32 - i)
  const perioData = useMemo(() => {
    return { ...activePerioChart.perio_data }
  }, [activePerioChart])

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      try {
        validatePerioData(PerioSection.Facial, perioData.facial)
        validatePerioData(PerioSection.Lingual, perioData.lingual)
      } catch (err) {
        logger.error('[Perio Validation Error]', err)
      }
    }
  }, [perioData])

  const getTripleValueCell = (
    section: PerioSection,
    label: 'PD' | 'GM' | 'CAL',
    tooth: number,
  ): React.ReactElement => {
    const index = getToothIndex(tooth)
    const raw = perioData[section]?.[label]?.[index] ?? '0 0 0'
    const value = toDisplayOrder(tooth, raw.split(' ')).join(' ')
    return <div className="triple-value-cell dbm-text-spacing">{value}</div>
  }

  const getSingleValueCell = (
    section: PerioSection,
    label: 'Mobility',
    index: number,
  ): React.ReactElement => {
    const rawValue = perioData[section]?.[label]?.[index] ?? '0'
    const value = rawValue?.split(' ')[0]
    return <div className="single-value-cell">{value}</div>
  }

  const getFillColor = (
    label: 'Bleeding' | 'Calculus' | 'Supp' | 'Furcation',
  ): string => {
    switch (label) {
      case 'Bleeding':
        return '#fe746b'
      case 'Calculus':
        return '#e3b016'
      case 'Supp':
        return '#65acf5'
      case 'Furcation':
        return '#e46910'
      default:
        return '#8A8E97'
    }
  }

  const isIconLabel = (
    label: string,
  ): label is 'Bleeding' | 'Calculus' | 'Supp' | 'Furcation' =>
    ['Bleeding', 'Calculus', 'Supp', 'Furcation'].includes(label)

  const isTripleValueLabel = (label: string): label is 'PD' | 'GM' | 'CAL' =>
    ['PD', 'GM', 'CAL'].includes(label)

  const renderTripleIconCell = (
    section: PerioSection,
    label: 'Bleeding' | 'Calculus' | 'Supp' | 'Furcation',
    tooth: number,
  ): React.ReactElement => {
    const index = getToothIndex(tooth)
    const value = perioData[section]?.[label]?.[index] ?? ''
    const activeSet = new Set(value?.split(' '))
    const fill = getFillColor(label)

    return (
      <div className="triple-icon-cell d-flex justify-content-center gap-1">
        {getProbeSiteLabels(tooth).map((surface, i) => {
          const isActive = activeSet.has(surface)
          const pathD =
            label === 'Furcation'
              ? 'M7.16 2.1C7.25 1.95 7.38 1.82 7.53 1.73C7.68 1.64 7.86 1.6 8.03 1.6C8.21 1.6 8.38 1.64 8.53 1.73C8.68 1.82 8.81 1.95 8.9 2.1L14.65 12.07C14.74 12.22 14.79 12.39 14.79 12.57C14.79 12.74 14.74 12.92 14.65 13.07C14.57 13.22 14.44 13.35 14.29 13.43C14.14 13.52 13.96 13.57 13.79 13.57H2.28C2.1 13.57 1.93 13.52 1.78 13.43C1.62 13.35 1.5 13.22 1.41 13.07C1.32 12.92 1.28 12.74 1.28 12.57C1.28 12.39 1.32 12.22 1.41 12.07L7.16 2.1Z'
              : 'M7.53 1.55C7.67 1.43 7.85 1.37 8.03 1.37C8.21 1.37 8.39 1.43 8.53 1.55C9.49 2.34 10.37 3.23 11.15 4.2C12.25 5.56 13.36 7.4 13.36 9.33C13.36 10.74 12.8 12.1 11.8 13.1C10.8 14.1 9.45 14.66 8.03 14.66C6.62 14.66 5.26 14.1 4.26 13.1C3.26 12.1 2.7 10.74 2.7 9.33C2.7 7.4 3.82 5.56 4.91 4.2C5.69 3.23 6.57 2.34 7.53 1.55Z'

          return (
            <svg
              key={i}
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                opacity: isActive ? 1 : 0.15,
                transition: 'opacity 0.2s ease',
              }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d={pathD}
                fill={fill}
              />
            </svg>
          )
        })}
      </div>
    )
  }

  const renderSectionRows = (
    section: PerioSection,
    label: string,
    teeth: number[],
  ): React.ReactElement => (
    <tr key={`${section}-${label}`}>
      <td className="label-cell">{label}</td>
      {teeth.map((tooth) => {
        const index = getToothIndex(tooth)
        const selected =
          voiceCursor?.tooth === tooth &&
          voiceCursor.section === section &&
          voiceCursor.metric === label
        return (
          <td
            key={tooth}
            ref={selected ? cursorCell : undefined}
            className={selected ? 'perio-voice-cursor-cell' : undefined}
            aria-current={selected ? 'true' : undefined}
            aria-label={
              selected
                ? `Voice cursor: Universal tooth ${tooth} ${section} ${label}`
                : undefined
            }
          >
            {isIconLabel(label)
              ? renderTripleIconCell(section, label, tooth)
              : isTripleValueLabel(label)
                ? getTripleValueCell(section, label, tooth)
                : label === 'Mobility'
                  ? getSingleValueCell(section, 'Mobility', index)
                  : null}
          </td>
        )
      })}
    </tr>
  )

  const renderTable = (
    section: PerioSection,
    teeth: number[],
    showDBMRow: boolean,
    showToothNumbers: boolean,
    labelAtBottom = false,
  ): React.ReactElement => (
    <table className="perio-chart-table">
      <thead>
        {showToothNumbers && (
          <tr>
            <th></th>
            {teeth.map((n) => (
              <th
                key={n}
                className={
                  voiceCursor?.tooth === n
                    ? 'perio-voice-cursor-tooth'
                    : undefined
                }
              >
                <ToothNumberLabel
                  universalToothNumber={n}
                  numberingSystem={chartingSystem.tooth_numbering_system}
                />
              </th>
            ))}
          </tr>
        )}
        {!labelAtBottom && (
          <tr>
            <td colSpan={17} className="section-label">
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </td>
          </tr>
        )}
        {showDBMRow && (
          <tr className="dbm-row">
            <td></td>
            {teeth.map((n) => (
              <td key={n} className="dbm-text-spacing">
                {getProbeSiteLabels(n).join(' ')}
              </td>
            ))}
          </tr>
        )}
      </thead>
      <tbody>
        {[
          'PD',
          'GM',
          'CAL',
          'Bleeding',
          'Calculus',
          'Supp',
          'Furcation',
          'Mobility',
        ].map((label) => renderSectionRows(section, label, teeth))}
        {labelAtBottom && (
          <tr>
            <td colSpan={17} className="section-label">
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )

  const handleSelectHistory = (row: RowSelectEventArgs): void => {
    const isDeselection = (row as RecordClickEventArgs).name === 'rowDeselected'
    const chartId = getChartHistoryRowId(row.data)
    const selectedChart = isDeselection
      ? null
      : perioCharts.find((chart) => chartId && chart.chart_id === chartId)
    if (selectedChart === undefined) return

    // Capture the stored chart before deferring; grid events may have no data
    // or be reused after the callback. Let selection DOM updates finish first.
    window.queueMicrotask(() => {
      // Viewing history is not an edit session — clear dirty so Save/Reset stay off.
      dispatch(setPerioUpdated(false))
      dispatch(setSelectedPerioChart(selectedChart))
    })
  }

  return (
    <div className="perio-page-container">
      {isFullscreen ? (
        <div className="perio-chart-wrapper">
          <div className="chart-table-wrapper">
            {renderTable('facial', toothNumbersUpper, true, showToothNumber)}
            {renderTable('lingual', toothNumbersUpper, false, false)}
          </div>
          <div className="chart-table-wrapper">
            {renderTable(
              'lingual',
              toothNumbersLower,
              true,
              showToothNumber,
              true,
            )}
            {renderTable('facial', toothNumbersLower, false, false, true)}
          </div>
        </div>
      ) : chartIndex === 0 ? (
        <div className="perio-chart-wrapper">
          {renderTable('facial', toothNumbersUpper, true, showToothNumber)}
          {renderTable('lingual', toothNumbersUpper, false, false)}
        </div>
      ) : (
        <div className="perio-chart-wrapper">
          <div className="chart-table-wrapper">
            {renderTable(
              'lingual',
              toothNumbersLower,
              true,
              showToothNumber,
              true,
            )}
          </div>
          <div className="chart-table-wrapper">
            {renderTable('facial', toothNumbersLower, false, false, true)}
          </div>
        </div>
      )}

      <div className="perio-history-wrapper">
        <PerioHistory
          onViewNote={onViewNote}
          onSelectHistory={handleSelectHistory}
          gridRef={gridRef}
        />
      </div>
    </div>
  )
}

export default PerioChart
